import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { motion } from 'framer-motion'
import { apiService } from '../../services/api'
import {
  Globe,
  CheckCircle,
  AlertCircle,
  Clock,
  XCircle,
  Search,
  Filter,
  Shield,
  Eye,
  RefreshCw,
  MoreHorizontal,
  Calendar,
  Building2,
  Server,
  Lock,
  Zap,
  Activity,
  Settings,
  ExternalLink,
  Copy,
  AlertTriangle,
  TrendingUp,
  ChevronRight,
  Database,
  Cloud,
  Edit,
  Trash2
} from 'lucide-react'
import toast from 'react-hot-toast'
import LoadingSpinner from '../common/LoadingSpinner'
import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { cn } from '../../utils/cn'

const DomainDashboard = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [selectedDomain, setSelectedDomain] = useState(null)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const queryClient = useQueryClient()

  // Fetch all domains (admin view)
  const { data: domainsData, isLoading, error, refetch } = useQuery(
    'admin-domains',
    () => apiService.get('/domains').then(res => res.data),
    {
      staleTime: 30000,
      refetchInterval: 60000
    }
  )

  const handleRefresh = async () => {
    console.log('Refresh clicked - setting isRefreshing to true')
    setIsRefreshing(true)
    
    // Ensure minimum animation time
    setTimeout(() => {
      console.log('Setting isRefreshing to false after timeout')
      setIsRefreshing(false)
    }, 1000)
    
    try {
      await refetch()
      console.log('Refetch completed')
    } catch (error) {
      console.error('Refresh error:', error)
    }
  }

  // Verify domain mutation
  const verifyDomainMutation = useMutation(
    (domainId) => apiService.post(`/domains/${domainId}/verify`),
    {
      onSuccess: (data, domainId) => {
        // Backend returns success response with message and domain object
        if (data.data.message && data.data.domain) {
          toast.success(data.data.message)
        } else {
          toast.error('Verification response was incomplete')
        }
        queryClient.invalidateQueries('admin-domains')
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Verification failed')
      }
    }
  )

  // Delete domain mutation
  const deleteDomainMutation = useMutation(
    (domainId) => apiService.delete(`/domains/${domainId}`),
    {
      onSuccess: () => {
        toast.success('Domain deleted successfully!')
        queryClient.invalidateQueries('admin-domains')
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to delete domain')
      }
    }
  )

  // Update domain settings mutation
  const updateDomainMutation = useMutation(
    ({ domainId, settings }) => apiService.patch(`/domains/${domainId}`, settings),
    {
      onSuccess: () => {
        toast.success('Domain settings updated successfully!')
        queryClient.invalidateQueries('admin-domains')
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to update domain settings')
      }
    }
  )

  // Extract domains array from API response
  const domains = domainsData?.domains || []
  
  // Filter domains
  const filteredDomains = domains?.filter(domain => {
    const fullDomainName = domain.subdomain ? `${domain.subdomain}.${domain.domain}` : domain.domain
    const matchesSearch = searchTerm === '' || 
      fullDomainName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      domain.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      domain.contact_email?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === 'all' || domain.status === statusFilter
    return matchesSearch && matchesStatus
  }) || []

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified':
      case 'active':
        return <CheckCircle className="h-4 w-4 text-success-500" />
      case 'pending':
        return <Clock className="h-4 w-4 text-accent-500" />
      case 'dns_configured':
        return <AlertCircle className="h-4 w-4 text-primary-500" />
      case 'failed':
        return <XCircle className="h-4 w-4 text-error-500" />
      default:
        return <Clock className="h-4 w-4 text-neutral-500" />
    }
  }

  const getDisplayDomain = (domain) => {
    if (domain.startsWith('www.')) {
      return domain.substring(4);
    }
    return domain;
  }

  const getStatusText = (status) => {
    switch (status) {
      case 'verified':
        return 'Verified'
      case 'active':
        return 'Active'
      case 'pending':
        return 'Pending Setup'
      case 'dns_configured':
        return 'DNS Configured'
      case 'failed':
        return 'Failed'
      default:
        return 'Unknown'
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified':
      case 'active':
        return 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-300'
      case 'pending':
        return 'bg-accent-100 text-accent-800 dark:bg-accent-900/20 dark:text-accent-300'
      case 'dns_configured':
        return 'bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-300'
      case 'failed':
        return 'bg-error-100 text-error-800 dark:bg-error-900/20 dark:text-error-300'
      default:
        return 'bg-neutral-100 text-neutral-800 dark:bg-neutral-800 dark:text-neutral-300'
    }
  }

  // Domain statistics
  const stats = domains ? {
    total: domains.length,
    verified: domains.filter(d => d.status === 'verified' || d.status === 'active').length,
    pending: domains.filter(d => d.status === 'pending').length,
    failed: domains.filter(d => d.status === 'failed').length,
    sslEnabled: domains.filter(d => d.ssl_enabled || d.verification_method === 'none').length
  } : null

  if (error) {
    return (
      <div className="pt-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-12"
        >
          <div className="mx-auto w-16 h-16 bg-error-100 dark:bg-error-900/20 rounded-full flex items-center justify-center mb-4">
            <XCircle className="h-8 w-8 text-error-600 dark:text-error-400" />
          </div>
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
            Error Loading Domains
          </h3>
          <p className="text-neutral-600 dark:text-neutral-400">
            {error.response?.data?.error || 'Failed to load domains'}
          </p>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="pt-4 space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex items-center justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-neutral-900 dark:text-neutral-100">
            Domain Management
          </h1>
          <p className="text-neutral-600 dark:text-neutral-400 mt-1">
            Manage custom domains across all clients
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="border-neutral-200 dark:border-[#2f2b41] bg-white dark:bg-[#221f2f] text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-[#2f2b41] hover:text-neutral-900 dark:hover:text-neutral-100"
          >
            <RefreshCw 
              className={cn(
                "h-4 w-4 mr-2", 
                isRefreshing && "animate-spin"
              )} 
              style={{ 
                animation: isRefreshing ? 'spin 1s linear infinite' : 'none'
              }}
            />
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
      </motion.div>

      {/* Statistics */}
      {stats && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4"
        >
          <StatCard
            title="Total Domains"
            value={stats.total}
            icon={Globe}
            color="primary"
            index={0}
          />
          <StatCard
            title="Verified"
            value={stats.verified}
            icon={CheckCircle}
            color="success"
            index={1}
          />
          <StatCard
            title="Pending"
            value={stats.pending}
            icon={Clock}
            color="accent"
            index={2}
          />
          <StatCard
            title="Failed"
            value={stats.failed}
            icon={XCircle}
            color="error"
            index={3}
          />
          <StatCard
            title="SSL Enabled"
            value={stats.sslEnabled}
            icon={Shield}
            color="secondary"
            index={4}
          />
        </motion.div>
      )}

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-400" />
                  <input
                    type="text"
                    placeholder="Search domains or clients..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-neutral-300 dark:border-[#2f2b41] rounded-md bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100 text-sm placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="sm:w-48">
                <div className="relative">
                  <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-400" />
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-neutral-300 dark:border-[#2f2b41] rounded-md bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent appearance-none cursor-pointer"
                  >
                    <option value="all">All Status</option>
                    <option value="verified">Verified</option>
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                    <option value="dns_configured">DNS Configured</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Domains Grid */}
      {isLoading ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex items-center justify-center py-12"
        >
          <LoadingSpinner size="lg" text="Loading domains..." />
        </motion.div>
      ) : filteredDomains.length > 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6"
        >
          {filteredDomains.map((domain, index) => (
            <DomainCard
              key={domain.id}
              domain={domain}
              onVerify={() => verifyDomainMutation.mutate(domain.id)}
              isVerifying={verifyDomainMutation.isLoading}
              onDelete={() => deleteDomainMutation.mutate(domain.id)}
              isDeleting={deleteDomainMutation.isLoading}
              onUpdate={(settings) => updateDomainMutation.mutate({ domainId: domain.id, settings })}
              isUpdating={updateDomainMutation.isLoading}
              index={index}
            />
          ))}
        </motion.div>
      ) : (
        <EmptyState searchTerm={searchTerm} statusFilter={statusFilter} />
      )}
    </div>
  )
}

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, color, index = 0 }) => {
  const colorClasses = {
    primary: 'bg-primary-500 text-white',
    success: 'bg-success-500 text-white',
    accent: 'bg-accent-500 text-white',
    error: 'bg-error-500 text-white',
    secondary: 'bg-secondary-500 text-white'
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      <Card className="hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-white to-neutral-50 dark:from-dark-200 dark:to-dark-300 border border-neutral-200 dark:border-neutral-700 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                {title}
              </p>
              <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                {value}
              </p>
            </div>
            <div className={cn('p-3 rounded-xl', colorClasses[color])}>
              <Icon className="h-6 w-6" />
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  )
}

// Domain Card Component
const DomainCard = ({ domain, onVerify, isVerifying, onDelete, isDeleting, onUpdate, isUpdating, index = 0 }) => {
  const [showMenu, setShowMenu] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showSettingsModal, setShowSettingsModal] = useState(false)
  const [domainSettings, setDomainSettings] = useState({
    ssl_enabled: domain.ssl_enabled || false,
    contact_email: domain.contact_email || '',
    status: domain.status || 'pending'
  })
  const fullDomain = domain.subdomain ? `${domain.subdomain}.${domain.domain}` : domain.domain
  
  // Function to display domain name with truncation and www removal
  const getDisplayDomain = (domainName, maxLength = 25) => {
    let displayName = domainName
    
    // Remove www. prefix if present
    if (displayName.startsWith('www.')) {
      displayName = displayName.substring(4)
    }
    
    // Truncate if too long
    if (displayName.length > maxLength) {
      return displayName.substring(0, maxLength - 3) + '...'
    }
    
    return displayName
  }
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
    toast.success('Copied to clipboard')
  }

  const handleMenuAction = (action) => {
    setShowMenu(false)
    switch (action) {
      case 'delete':
        setShowDeleteConfirm(true)
        break
      case 'settings':
        setShowSettingsModal(true)
        break
      default:
        break
    }
  }

  const handleDeleteConfirm = () => {
    setShowDeleteConfirm(false)
    onDelete()
  }

  const handleSettingsUpdate = () => {
    // Only send changed fields
    const updates = {}
    if (domainSettings.ssl_enabled !== domain.ssl_enabled) {
      updates.ssl_enabled = domainSettings.ssl_enabled
    }
    if (domainSettings.contact_email !== domain.contact_email) {
      updates.contact_email = domainSettings.contact_email
    }
    if (domainSettings.status !== domain.status) {
      updates.status = domainSettings.status
    }

    if (Object.keys(updates).length > 0) {
      onUpdate(updates)
      setShowSettingsModal(false)
    } else {
      toast('No changes to save')
      setShowSettingsModal(false)
    }
  }

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMenu && !event.target.closest('.menu-container')) {
        setShowMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showMenu])

  // Close modals when clicking outside
  React.useEffect(() => {
    const handleModalClickOutside = (event) => {
      // Check for delete confirmation modal
      if (showDeleteConfirm && !event.target.closest('.modal-content')) {
        setShowDeleteConfirm(false)
      }
      // Check for settings modal
      if (showSettingsModal && !event.target.closest('.modal-content')) {
        setShowSettingsModal(false)
      }
    }
    
    if (showDeleteConfirm || showSettingsModal) {
      document.addEventListener('mousedown', handleModalClickOutside)
      return () => document.removeEventListener('mousedown', handleModalClickOutside)
    }
  }, [showDeleteConfirm, showSettingsModal])

  // Reset settings when modal opens
  React.useEffect(() => {
    if (showSettingsModal) {
      setDomainSettings({
        ssl_enabled: domain.ssl_enabled || false,
        contact_email: domain.contact_email || '',
        status: domain.status || 'pending'
      })
    }
  }, [showSettingsModal, domain])

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified':
      case 'active':
        return <CheckCircle className="h-4 w-4 text-success-500" />
      case 'pending':
        return <Clock className="h-4 w-4 text-accent-500" />
      case 'dns_configured':
        return <AlertCircle className="h-4 w-4 text-primary-500" />
      case 'failed':
        return <XCircle className="h-4 w-4 text-error-500" />
      default:
        return <Clock className="h-4 w-4 text-neutral-500" />
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified':
      case 'active':
        return 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-300'
      case 'pending':
        return 'bg-accent-100 text-accent-800 dark:bg-accent-900/20 dark:text-accent-300'
      case 'dns_configured':
        return 'bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-300'
      case 'failed':
        return 'bg-error-100 text-error-800 dark:bg-error-900/20 dark:text-error-300'
      default:
        return 'bg-neutral-100 text-neutral-800 dark:bg-neutral-800 dark:text-neutral-300'
    }
  }

  const getStatusText = (status) => {
    switch (status) {
      case 'verified':
        return 'Verified'
      case 'active':
        return 'Active'
      case 'pending':
        return 'Pending Setup'
      case 'dns_configured':
        return 'DNS Configured'
      case 'failed':
        return 'Failed'
      default:
        return 'Unknown'
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      <Card className="hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-white to-neutral-50 dark:from-dark-200 dark:to-dark-300 border border-neutral-200 dark:border-neutral-700 shadow-sm h-full flex flex-col">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-3 flex-1 min-w-0">
              <div className="p-2 bg-primary-100 dark:bg-primary-900/20 rounded-lg">
                <Globe className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center space-x-2 justify-between">
                  <div className="flex items-center space-x-2 min-w-0">
                    <div className="relative group">
                      <h3 className="font-semibold text-neutral-900 dark:text-neutral-100 truncate">
                        {getDisplayDomain(fullDomain)}
                      </h3>
                      {/* Tooltip for full domain */}
                      <div className="absolute left-0 top-full mt-1 bg-neutral-900 dark:bg-neutral-800 text-white px-2 py-1 rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10 shadow-md">
                        {fullDomain}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(fullDomain)}
                      className="h-6 w-6 p-0 hover:bg-neutral-100 dark:hover:bg-neutral-800 flex-shrink-0"
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                  <span className={cn(
                    'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0',
                    getStatusColor(domain.status)
                  )}>
                    {getStatusIcon(domain.status)}
                    <span className="ml-1">{getStatusText(domain.status)}</span>
                  </span>
                </div>
                {domain.subdomain && (
                  <div className="relative group">
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
                      Subdomain of {domain.domain}
                    </p>
                    {/* Tooltip for full subdomain text */}
                    <div className="absolute left-0 top-full mt-1 bg-neutral-900 dark:bg-neutral-800 text-white px-2 py-1 rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10 shadow-md">
                      Subdomain of {domain.domain}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="pt-0 space-y-4 flex-grow flex flex-col">
          {/* Client Information */}
          <div className="flex items-center space-x-3 p-3 bg-neutral-50 dark:bg-dark-300 rounded-lg">
            <Building2 className="h-4 w-4 text-neutral-500 dark:text-neutral-400" />
            <div>
              <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                {domain.company_name}
              </p>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                {domain.contact_email}
              </p>
            </div>
          </div>

          {/* SSL Information */}
          <div className="flex items-center justify-between p-3 bg-neutral-50 dark:bg-dark-300 rounded-lg">
            <div className="flex items-center space-x-2">
              <Shield className="h-4 w-4 text-neutral-500 dark:text-neutral-400" />
              <span className="text-sm text-neutral-700 dark:text-neutral-300">SSL Certificate</span>
            </div>
            <div className="text-right">
              {(domain.ssl_enabled || domain.verification_method === 'none') ? (
                <div className="flex items-center text-success-600 dark:text-success-400">
                  <Lock className="h-4 w-4 mr-1" />
                  <span className="text-sm font-medium">Enabled</span>
                </div>
              ) : (
                <span className="text-sm text-neutral-500 dark:text-neutral-400">Disabled</span>
              )}
              <div className="text-xs text-neutral-500 dark:text-neutral-400 min-h-[16px]">
                {domain.verification_method === 'none' ? (
                  'Via Cloudflare'
                ) : domain.ssl_expires_at ? (
                  `Expires: ${new Date(domain.ssl_expires_at).toLocaleDateString()}`
                ) : (
                  <span className="opacity-0">placeholder</span>
                )}
              </div>
            </div>
          </div>

          {/* DNS Information */}
          <div className="flex items-center justify-between p-3 bg-neutral-50 dark:bg-dark-300 rounded-lg">
            <div className="flex items-center space-x-2">
              <Server className="h-4 w-4 text-neutral-500 dark:text-neutral-400" />
              <span className="text-sm text-neutral-700 dark:text-neutral-300">DNS Status</span>
            </div>
            <div className="text-right">
              {domain.dns_configured || domain.status === 'active' || domain.status === 'dns_configured' ? (
                <div className="flex items-center text-primary-600 dark:text-primary-400">
                  <Database className="h-4 w-4 mr-1" />
                  <span className="text-sm font-medium">Configured</span>
                </div>
              ) : (
                <span className="text-sm text-neutral-500 dark:text-neutral-400">Pending</span>
              )}
            </div>
          </div>

          {/* Assigned Sites Information */}
          {domain.assigned_sites && domain.assigned_sites.length > 0 && (
            <div className="p-3 bg-accent-50 dark:bg-accent-900/20 rounded-lg border border-accent-200 dark:border-accent-700">
              <div className="flex items-start space-x-2">
                <Globe className="h-4 w-4 text-accent-600 dark:text-accent-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-accent-800 dark:text-accent-300">
                    Used by {domain.assigned_sites.length} site{domain.assigned_sites.length > 1 ? 's' : ''}
                  </p>
                  <div className="mt-1 space-y-1">
                    {domain.assigned_sites.map(site => (
                      <div key={site.id} className="flex items-center space-x-2">
                        <span className="text-xs text-accent-600 dark:text-accent-400 font-medium">
                          {site.name}
                        </span>
                        <span className={cn(
                          'inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium',
                          site.status === 'published' 
                            ? 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-300'
                            : 'bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300'
                        )}>
                          {site.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Date Information */}
          <div className="flex items-center justify-between text-sm text-neutral-500 dark:text-neutral-400 min-h-[24px]">
            <div className="flex items-center space-x-2">
              <Calendar className="h-4 w-4" />
              <span>Added: {new Date(domain.created_at).toLocaleDateString()}</span>
            </div>
            <div className="min-w-0">
              {domain.verified_at ? (
                <div className="flex items-center space-x-2 text-success-600 dark:text-success-400">
                  <CheckCircle className="h-4 w-4" />
                  <span>Verified: {new Date(domain.verified_at).toLocaleDateString()}</span>
                </div>
              ) : (
                <div className="h-[24px]"></div>
              )}
            </div>
          </div>

          {/* Error Message */}
          {domain.error_message && (
            <div className="p-3 bg-error-50 dark:bg-error-900/20 rounded-lg border border-error-200 dark:border-error-800">
              <div className="flex items-start space-x-2">
                <AlertTriangle className="h-4 w-4 text-error-600 dark:text-error-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-error-800 dark:text-error-300">Error</p>
                  <p className="text-xs text-error-600 dark:text-error-400">{domain.error_message}</p>
                </div>
              </div>
            </div>
          )}
          
          {/* Actions */}
          <div className="flex items-center justify-between pt-2 mt-auto">
            <div className="flex items-center space-x-2">
              {(domain.status === 'pending' || domain.status === 'failed') && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onVerify}
                  disabled={isVerifying}
                >
                  {isVerifying ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Verify
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => window.open(`https://${fullDomain}`, '_blank')}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Visit
              </Button>
            </div>
            <div className="relative menu-container">
              <Button
                variant="ghost"
                size="sm"
                className="p-2"
                onClick={() => setShowMenu(!showMenu)}
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
              
              {showMenu && (
                <div className="absolute right-0 top-8 w-48 bg-white dark:bg-[#221f2f] rounded-lg shadow-lg border border-neutral-200 dark:border-[#2f2b41] py-2 z-10">
                  <button
                    onClick={() => handleMenuAction('settings')}
                    className="w-full px-4 py-2 text-left text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-[#2f2b41] flex items-center"
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Domain Settings
                  </button>
                  <button
                    onClick={() => handleMenuAction('delete')}
                    className="w-full px-4 py-2 text-left text-sm text-error-600 dark:text-error-400 hover:bg-error-50 dark:hover:bg-error-900/20 flex items-center"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Domain
                  </button>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="modal-content bg-white dark:bg-[#221f2f] rounded-lg p-6 max-w-md w-full mx-4 border border-neutral-200 dark:border-[#2f2b41]">
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-error-100 dark:bg-error-900/20 rounded-full">
              <AlertTriangle className="w-6 h-6 text-error-600 dark:text-error-400" />
            </div>
            
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-2 text-center">
              Delete Domain
            </h3>
            
            {domain.assigned_sites && domain.assigned_sites.length > 0 ? (
              <div className="mb-4">
                <div className="p-3 bg-error-50 dark:bg-error-900/20 rounded-lg border border-error-200 dark:border-error-700 mb-4">
                  <p className="text-sm font-medium text-error-800 dark:text-error-300 mb-2">
                    ⚠️ Cannot delete domain in use
                  </p>
                  <p className="text-xs text-error-600 dark:text-error-400">
                    The domain <strong>{fullDomain}</strong> is currently assigned to {domain.assigned_sites.length} site{domain.assigned_sites.length > 1 ? 's' : ''}:
                  </p>
                  <ul className="mt-2 space-y-1">
                    {domain.assigned_sites.map(site => (
                      <li key={site.id} className="text-xs text-error-600 dark:text-error-400">
                        • {site.name} ({site.status})
                      </li>
                    ))}
                  </ul>
                </div>
                <p className="text-neutral-600 dark:text-neutral-400 text-sm text-center">
                  Please unassign this domain from all sites before deleting it.
                </p>
              </div>
            ) : (
              <p className="text-neutral-600 dark:text-neutral-400 mb-4 text-center">
                Are you sure you want to delete <strong>{fullDomain}</strong>? This action cannot be undone.
              </p>
            )}
            
            <div className="flex space-x-3 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(false)}
                className="border-neutral-200 dark:border-[#2f2b41] bg-white dark:bg-[#221f2f] text-neutral-700 dark:text-neutral-300"
              >
                Cancel
              </Button>
              {!(domain.assigned_sites && domain.assigned_sites.length > 0) && (
                <Button
                  variant="destructive"
                  onClick={handleDeleteConfirm}
                  disabled={isDeleting}
                  className="bg-error-600 hover:bg-error-700 text-white"
                >
                  {isDeleting ? <LoadingSpinner size="sm" /> : 'Delete'}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettingsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="modal-content bg-white dark:bg-[#221f2f] rounded-lg p-6 max-w-lg w-full mx-4 border border-neutral-200 dark:border-[#2f2b41]">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
              Domain Settings - {fullDomain}
            </h3>
            
            <div className="space-y-6">
              {/* Domain Info */}
              <div className="p-4 bg-neutral-50 dark:bg-[#2f2b41] rounded-lg">
                <h4 className="font-medium text-neutral-900 dark:text-neutral-100 mb-2">Domain Information</h4>
                <div className="text-sm text-neutral-600 dark:text-neutral-400 space-y-1">
                  <div>Domain: <span className="font-mono">{fullDomain}</span></div>
                  <div>Company: {domain.company_name}</div>
                  <div>Created: {new Date(domain.created_at).toLocaleDateString()}</div>
                  {domain.verified_at && (
                    <div>Verified: {new Date(domain.verified_at).toLocaleDateString()}</div>
                  )}
                </div>
              </div>

              {/* SSL Settings — only relevant for custom domains, not managed subdomains */}
              {domain.verification_method !== 'none' && (
                <div>
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={domainSettings.ssl_enabled}
                      onChange={(e) => setDomainSettings(prev => ({ ...prev, ssl_enabled: e.target.checked }))}
                      className="w-4 h-4 text-accent-600 bg-neutral-100 border-neutral-300 rounded focus:ring-accent-500 dark:bg-neutral-700 dark:border-neutral-600"
                    />
                    <div>
                      <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">Enable SSL Certificate</span>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">Automatically provision and manage SSL certificate</p>
                    </div>
                  </label>
                </div>
              )}

              {/* Contact Email */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Contact Email
                </label>
                <input
                  type="email"
                  value={domainSettings.contact_email}
                  onChange={(e) => setDomainSettings(prev => ({ ...prev, contact_email: e.target.value }))}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-[#2f2b41] rounded-md bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100 placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent"
                  placeholder="contact@example.com"
                />
              </div>

              {/* Domain Status */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Domain Status
                </label>
                <select
                  value={domainSettings.status}
                  onChange={(e) => setDomainSettings(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-[#2f2b41] rounded-md bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent"
                >
                  <option value="pending">Pending</option>
                  <option value="dns_configured">DNS Configured</option>
                  <option value="verified">Verified</option>
                  <option value="active">Active</option>
                  <option value="failed">Failed</option>
                </select>
              </div>

              {/* DNS Configuration */}
              <div className="p-4 bg-neutral-50 dark:bg-[#2f2b41] rounded-lg">
                <h4 className="font-medium text-neutral-900 dark:text-neutral-100 mb-3">DNS Configuration</h4>

                {domain.verification_method === 'none' ? (
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">
                    This subdomain is managed by WebNest — no DNS setup required.
                  </p>
                ) : domain.cf_fallback_hostname ? (
                  <div className="space-y-2">
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">Add these records at your domain registrar:</p>
                    <div className="font-mono text-xs bg-white dark:bg-[#221f2f] text-neutral-700 dark:text-neutral-300 p-3 rounded border border-neutral-200 dark:border-[#3a3650] space-y-1">
                      <div className="text-neutral-400 dark:text-neutral-500 uppercase tracking-wide text-xs mb-1">CNAME</div>
                      <div><span className="text-neutral-400 dark:text-neutral-500">Name:</span> {domain.subdomain || '@'}</div>
                      <div><span className="text-neutral-400 dark:text-neutral-500">Value:</span> {domain.cf_fallback_hostname}</div>
                    </div>
                    {domain.cf_ownership_txt_name && (
                      <div className="font-mono text-xs bg-white dark:bg-[#221f2f] text-neutral-700 dark:text-neutral-300 p-3 rounded border border-neutral-200 dark:border-[#3a3650] space-y-1">
                        <div className="text-neutral-400 dark:text-neutral-500 uppercase tracking-wide text-xs mb-1">TXT (ownership verification)</div>
                        <div><span className="text-neutral-400 dark:text-neutral-500">Name:</span> {domain.cf_ownership_txt_name}</div>
                        <div className="break-all"><span className="text-neutral-400 dark:text-neutral-500">Value:</span> {domain.cf_ownership_txt_value}</div>
                      </div>
                    )}
                    <p className="text-xs text-neutral-400 dark:text-neutral-500">SSL is provisioned automatically once DNS propagates.</p>
                  </div>
                ) : domain.verification_token ? (
                  <div className="space-y-2">
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">Add these records at your domain registrar:</p>
                    <div className="font-mono text-xs bg-white dark:bg-[#221f2f] text-neutral-700 dark:text-neutral-300 p-3 rounded border border-neutral-200 dark:border-[#3a3650] space-y-1">
                      <div className="text-neutral-400 dark:text-neutral-500 uppercase tracking-wide text-xs mb-1">TXT (verification)</div>
                      <div><span className="text-neutral-400 dark:text-neutral-500">Name:</span> _webnest-verify.{fullDomain}</div>
                      <div className="break-all"><span className="text-neutral-400 dark:text-neutral-500">Value:</span> webnest-verify={domain.verification_token}</div>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">
                    No DNS instructions available for this domain.
                  </p>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-between items-center pt-4 border-t border-neutral-200 dark:border-[#2f2b41]">
                <div>
                  {(domain.status === 'pending' || domain.status === 'failed') && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        onVerify()
                        setShowSettingsModal(false)
                      }}
                      disabled={isVerifying}
                      className="border-accent-300 dark:border-accent-600 text-accent-600 dark:text-accent-400 hover:bg-accent-50 dark:hover:bg-accent-900/20"
                    >
                      {isVerifying ? <LoadingSpinner size="sm" /> : (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Force Verify
                        </>
                      )}
                    </Button>
                  )}
                </div>
                <div className="flex space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowSettingsModal(false)}
                    className="border-neutral-200 dark:border-[#2f2b41] bg-white dark:bg-[#221f2f] text-neutral-700 dark:text-neutral-300"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSettingsUpdate}
                    disabled={isUpdating}
                    className="bg-accent-600 hover:bg-accent-700 text-white"
                  >
                    {isUpdating ? <LoadingSpinner size="sm" /> : 'Save Changes'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  )
}

// Empty State Component
const EmptyState = ({ searchTerm, statusFilter }) => {
  const isFiltered = searchTerm || statusFilter !== 'all'
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="text-center py-12"
    >
      <Card className="max-w-md mx-auto">
        <CardContent className="pt-6">
          <div className="mx-auto w-16 h-16 bg-neutral-100 dark:bg-neutral-800 rounded-full flex items-center justify-center mb-6">
            <Globe className="h-8 w-8 text-neutral-400" />
          </div>
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
            {isFiltered ? 'No domains match your filters' : 'No custom domains yet'}
          </h3>
          <p className="text-neutral-600 dark:text-neutral-400 mb-6">
            {isFiltered 
              ? 'Try adjusting your search or filter criteria'
              : 'Domains will appear here when clients add them'
            }
          </p>
          {!isFiltered && (
            <div className="space-y-3">
              <div className="flex items-center justify-center space-x-4 text-sm text-neutral-500 dark:text-neutral-400">
                <div className="flex items-center space-x-2">
                  <Cloud className="h-4 w-4" />
                  <span>DNS Management</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Shield className="h-4 w-4" />
                  <span>SSL Certificates</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Activity className="h-4 w-4" />
                  <span>Domain Verification</span>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  )
}


export default DomainDashboard